import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { kuendigungenApi } from '../api';

const tabs = [
  { id: 'uebersicht', label: 'Übersicht' },
  { id: 'vorgaenge', label: 'Vorgänge' },
  { id: 'bald', label: 'Bald kündbar' },
  { id: 'empfohlen', label: 'Empfohlen' },
  { id: 'vorbereitet', label: 'Vorbereitet' },
  { id: 'versendet', label: 'Versendet' },
  { id: 'abgeschlossen', label: 'Abgeschlossen' },
  { id: 'archiv', label: 'Archiv' },
];

const VORGANG_STATUS_LABELS = {
  entwurf: 'Entwurf', vorbereitet: 'Vorbereitet', freigegeben: 'Freigegeben',
  versendet: 'Versendet', antwort_erhalten: 'Antwort erhalten', bestaetigt: 'Bestätigt',
  abgeschlossen: 'Abgeschlossen', archiviert: 'Archiviert', zurueckgezogen: 'Zurückgezogen',
};
const VORGANG_STATUS_TONE = {
  entwurf: '#64748b', vorbereitet: '#38bdf8', freigegeben: '#22d3ee',
  versendet: '#34d399', antwort_erhalten: '#fbbf24', bestaetigt: '#a3e635',
  abgeschlossen: '#10b981', archiviert: '#94a3b8', zurueckgezogen: '#f87171',
};

const wizardSteps = [
  'Vertrag prüfen',
  'Vorlage wählen',
  'Vorschau',
  'Prüfen',
  'Fertig',
];

const BG_PANEL = '#1e293b';
const BORDER = '#334155';
const TEXT_BODY = '#94a3b8';
const EMPTY_STATE_TEXT = { vertrage: 'Keine Verträge vorhanden', dokumente: 'Keine Dokumente vorhanden', kundigungen: 'Keine Kündigungen vorhanden' };

export default function KuendigungenPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('uebersicht');
  const [wizard, setWizard] = useState(null);
  const [vorgaenge, setVorgaenge] = useState([]);
  const [vorgaengeLoading, setVorgaengeLoading] = useState(false);
  const navigate = useNavigate();

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await kuendigungenApi.list();
      setData(res.data);
    } catch {
      setError('Kündigungscenter konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const loadVorgaenge = useCallback(async () => {
    setVorgaengeLoading(true);
    try {
      const res = await kuendigungenApi.vorgaenge.list();
      setVorgaenge(res.data?.vorgaenge || []);
    } catch {
      // ignore — graceful fallback
    } finally {
      setVorgaengeLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'vorgaenge') loadVorgaenge();
  }, [activeTab, loadVorgaenge]);

  const overview = data?.ueberblick || {};
  const vorlagen = data?.vorlagen || [];
  const groups = useMemo(() => ({
    bald: data?.baldKuendbar || [],
    empfohlen: data?.empfohlen || [],
    vorbereitet: data?.vorbereitet || [],
    versendet: data?.versendet || [],
    abgeschlossen: data?.abgeschlossen || [],
    archiv: data?.archiv || [],
  }), [data]);

  const currentItems = activeTab === 'uebersicht'
    ? data?.fristen || []
    : groups[activeTab] || [];

  const openWizard = (vertrag) => {
    setWizard({
      step: 0,
      vertrag,
      vorlage: vorlagen[0]?.id || 'standard',
      preview: null,
      loading: false,
      error: '',
      completed: false,
    });
  };

  const updateWizard = (patch) => setWizard(current => current ? { ...current, ...patch } : current);

  const loadPreview = async (nextStep = 2) => {
    if (!wizard?.vertrag?.id) return;
    updateWizard({ loading: true, error: '' });
    try {
      const res = await kuendigungenApi.vorschau(wizard.vertrag.id, wizard.vorlage);
      updateWizard({ preview: res.data, loading: false, step: nextStep });
    } catch {
      updateWizard({ loading: false, error: 'Vorschau konnte nicht erstellt werden.' });
    }
  };

  const closeWizard = () => setWizard(null);

  if (loading) return <StateScreen text="Kündigungscenter wird geladen..." />;
  if (error) return <StateScreen text={error} />;
  if (!data) return <StateScreen text="Keine Kündigungsdaten vorhanden." />;
  if (currentItems.length === 0) {
    return (
      <div style={{ backgroundColor: BG_PANEL, border: `1px solid ${BORDER}`, color: TEXT_BODY, padding: '2rem', textAlign: 'center', margin: '2rem' }}>
        {EMPTY_STATE_TEXT.kundigungen}
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <section style={heroStyle}>
        <div style={{ minWidth: 0 }}>
          <div style={eyebrow}>Kündigungscenter Professional</div>
          <h1 style={heroTitle}>Kündigungen vollständig vorbereiten</h1>
          <p style={heroText}>
            Verträge prüfen, passende Vorlage wählen und eine PDF-fähige Kündigungsstruktur vorbereiten.
          </p>
        </div>
        <div style={heroKpis}>
          <KpiCard label="Bald kündbar" value={overview.bald_kuendbar || 0} tone="#fbbf24" />
          <KpiCard label="Empfohlen" value={(data.empfohlen || []).length} tone="#f87171" />
          <KpiCard label="Vorbereitet" value={overview.vorbereitet || 0} tone="#38bdf8" />
          <KpiCard label="Versendet" value={overview.versendet || 0} tone="#34d399" />
          <KpiCard label="Archiv" value={overview.archiv || 0} tone="#94a3b8" />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn-primary" onClick={() => navigate('/assistant?message=Bewerte meine Kündigungen und Fristen')}>🤖 Kündigung bewerten</button>
          <button className="btn-secondary" onClick={() => navigate('/kuendigungen/vorlagen')}>📋 Vorlagen</button>
        </div>
      </section>

      <section style={dashboardBand}>
        <DashboardMetric label="Aktive Verträge" value={overview.aktive_vertraege || 0} />
        <DashboardMetric label="Kündbar" value={overview.kuendbar || 0} />
        <DashboardMetric label="Abgeschlossen" value={overview.abgeschlossen || 0} />
        <DashboardMetric label="Dokumente" value={overview.dokumente || 0} />
        <DashboardMetric label="OCR Dokumente" value={overview.ocr_dokumente || 0} />
      </section>

      <div style={tabBar}>
        {tabs.map(tab => (
          <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} style={tabButton(activeTab === tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'uebersicht' ? (
        <div style={sectionGrid}>
          <Section title="Bald kündbar" count={groups.bald.length}>
            <ContractGrid items={groups.bald.slice(0, 4)} onPrepare={openWizard} />
          </Section>
          <Section title="Empfohlene Kündigungen" count={groups.empfohlen.length}>
            <ContractGrid items={groups.empfohlen.slice(0, 4)} onPrepare={openWizard} />
          </Section>
          <Section title="Bereits vorbereitet" count={groups.vorbereitet.length}>
            <ContractGrid items={groups.vorbereitet.slice(0, 4)} onPrepare={openWizard} />
          </Section>
          <Section title="Bereits versendet" count={groups.versendet.length}>
            <ContractGrid items={groups.versendet.slice(0, 4)} onPrepare={openWizard} />
          </Section>
          <Section title="Abgeschlossen" count={groups.abgeschlossen.length}>
            <ContractGrid items={groups.abgeschlossen.slice(0, 4)} onPrepare={openWizard} />
          </Section>
          <Section title="Archiv" count={groups.archiv.length}>
            <ContractGrid items={groups.archiv.slice(0, 4)} onPrepare={openWizard} />
          </Section>
        </div>
      ) : activeTab === 'vorgaenge' ? (
        <Section title="Kündigungsvorgänge" count={vorgaenge.length}>
          {vorgaengeLoading ? <StateScreen text="Vorgänge werden geladen..." /> : (
            vorgaenge.length === 0
              ? <EmptyState text="Noch keine Kündigungsvorgänge. Erstellen Sie einen Vorgang über 'Kündigung vorbereiten'." />
              : <div style={contractGrid}>
                  {vorgaenge.map(v => {
                    const tone = VORGANG_STATUS_TONE[v.status] || '#94a3b8';
                    return (
                      <article key={v.id} style={{ ...contractCard, borderColor: `${tone}66`, cursor: 'pointer' }} onClick={() => navigate(`/kuendigungen/vorgaenge/${v.id}`)}>
                        <div style={cardTop}>
                          <span style={{ ...badge, background: `${tone}22`, color: tone }}>{VORGANG_STATUS_LABELS[v.status] || v.status}</span>
                          {v.ki_hinweis && <span style={{ ...badge, background: '#fbbf2422', color: '#fbbf24' }}>💡 KI-Hinweis</span>}
                        </div>
                        <div style={provider}>{v.vertrag_anbieter || '—'}</div>
                        <h3 style={contractTitle}>{v.vertrag_titel || `Vertrag #${v.vertrag_id}`}</h3>
                        <div style={infoGrid}>
                          <Info label="Kündigung am" value={v.kuendigungsdatum || 'Offen'} />
                          <Info label="Sonderkündigung" value={v.sonderkuendigung ? 'Ja' : 'Nein'} />
                          <Info label="Versand" value={v.versandart || '—'} />
                          <Info label="Erstellt" value={v.erstellt_am?.split('T')[0] || '—'} />
                        </div>
                        <button type="button" className="btn-primary" style={{width:'100%'}} onClick={(e) => { e.stopPropagation(); navigate(`/kuendigungen/vorgaenge/${v.id}`); }}>
                          Vorgang öffnen →
                        </button>
                      </article>
                    );
                  })}
                </div>
          )}
        </Section>
      ) : (
        <Section title={tabs.find(tab => tab.id === activeTab)?.label || 'Kündigungen'} count={currentItems.length}>
          <ContractGrid items={currentItems} onPrepare={openWizard} />
        </Section>
      )}

      <Section title="Fristenübersicht" count={(data.fristen || []).length}>
        <div style={deadlineList}>
          {(data.fristen || []).slice(0, 10).map(item => (
            <DeadlineRow key={item.id} item={item} onPrepare={openWizard} />
          ))}
        </div>
      </Section>

      <Section title="Vorbereitungshistorie" count={(data.historie || []).length}>
        <HistoryList items={data.historie || []} />
      </Section>

      {wizard && (
        <WizardModal
          wizard={wizard}
          templates={vorlagen}
          onClose={closeWizard}
          onChangeTemplate={(vorlage) => updateWizard({ vorlage, preview: null })}
          onNext={() => {
            if (wizard.step === 1 || (wizard.step === 2 && !wizard.preview)) {
              loadPreview(wizard.step === 1 ? 2 : wizard.step);
              return;
            }
            if (wizard.step === 3) {
              updateWizard({ step: 4, completed: true });
              return;
            }
            updateWizard({ step: Math.min(wizard.step + 1, wizardSteps.length - 1) });
          }}
          onBack={() => updateWizard({ step: Math.max(wizard.step - 1, 0) })}
          onReloadPreview={() => loadPreview(2)}
        />
      )}
    </div>
  );
}

function ContractGrid({ items, onPrepare }) {
  if (!items || items.length === 0) return <EmptyState text="Keine Verträge in diesem Bereich." />;
  return (
    <div style={contractGrid}>
      {items.map(item => <ContractCard key={item.id} item={item} onPrepare={onPrepare} />)}
    </div>
  );
}

function ContractCard({ item, onPrepare }) {
  const tone = toneMap(item.status_tone || item.kuendigungsstatus);
  return (
    <article style={{ ...contractCard, borderColor: `${tone}66` }}>
      <div style={cardTop}>
        <span style={{ ...trafficLight, background: tone }} title={item.ampelstatus || item.status_label} />
        <span style={{ ...badge, background: `${tone}22`, color: tone }}>{item.status_label || item.kuendigungsstatus}</span>
        <span style={daysBadge}>{item.restlaufzeit_tage === null ? 'Frist offen' : `${item.restlaufzeit_tage} Tage`}</span>
      </div>
      <div style={provider}>{item.anbieter || 'Ohne Anbieter'}</div>
      <h3 style={contractTitle}>{item.titel}</h3>
      <div style={infoGrid}>
        <Info label="Kosten" value={`${formatEuro(item.kosten)} ${item.zahlungsintervall || ''}`} />
        <Info label="Monat" value={formatEuro(item.monatliche_kosten)} />
        <Info label="Jahr" value={formatEuro(item.jahreskosten)} />
        <Info label="Frist" value={item.frist_text || 'Nicht hinterlegt'} />
        <Info label="Vertragsnummer" value={item.vertragsnummer || 'Nicht hinterlegt'} />
        <Info label="Kundennummer" value={item.kundennummer || 'Nicht hinterlegt'} />
      </div>
      <button type="button" onClick={() => onPrepare(item)} className="btn-primary" style={{width:'100%'}}>
        Kündigung vorbereiten
      </button>
    </article>
  );
}

function WizardModal({ wizard, templates, onClose, onChangeTemplate, onNext, onBack, onReloadPreview }) {
  const preview = wizard.preview?.vorschau;
  const canBack = wizard.step > 0;
  const nextLabel = wizard.step === 4 ? 'Schließen' : wizard.step === 3 ? 'Fertigstellen' : 'Weiter';
  const handleNext = () => {
    if (wizard.step === 4) onClose();
    else onNext();
  };

  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={modalCard} onClick={event => event.stopPropagation()}>
        <div style={modalHeader}>
          <div>
            <div style={eyebrow}>Kündigungswizard</div>
            <h2 style={modalTitle}>{wizard.vertrag.titel}</h2>
          </div>
          <button type="button" onClick={onClose} className="btn-secondary">Schließen</button>
        </div>

        <div style={stepper}>
          {wizardSteps.map((label, index) => (
            <div key={label} style={stepItem(index <= wizard.step)}>
              <span style={stepCircle(index <= wizard.step)}>{index + 1}</span>
              <span>{label}</span>
            </div>
          ))}
        </div>

        {wizard.error && <EmptyState text={wizard.error} />}
        {wizard.loading && <EmptyState text="Vorschau wird erstellt..." />}

        {!wizard.loading && (
          <div style={wizardBody}>
            {wizard.step === 0 && <ReviewContractStep item={wizard.vertrag} />}
            {wizard.step === 1 && <TemplateStep templates={templates} selected={wizard.vorlage} onSelect={onChangeTemplate} />}
            {wizard.step === 2 && <PreviewStep preview={preview} onReload={onReloadPreview} />}
            {wizard.step === 3 && <CheckStep preview={preview} />}
            {wizard.step === 4 && <DoneStep preview={preview} />}
          </div>
        )}

        <div style={wizardFooter}>
          <button type="button" onClick={onBack} disabled={!canBack || wizard.loading} style={secondaryButton(!canBack || wizard.loading)}>
            Zurück
          </button>
          <button type="button" onClick={handleNext} disabled={wizard.loading} className="btn-primary">
            {nextLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewContractStep({ item }) {
  return (
    <div style={twoColumn}>
      <InfoPanel title="Vertragsdaten">
        <Info label="Anbieter" value={item.anbieter || 'Nicht hinterlegt'} />
        <Info label="Vertrag" value={item.titel} />
        <Info label="Vertragsnummer" value={item.vertragsnummer || 'Nicht hinterlegt'} />
        <Info label="Kundennummer" value={item.kundennummer || 'Nicht hinterlegt'} />
      </InfoPanel>
      <InfoPanel title="Fristen und Kosten">
        <Info label="Kündigungsfrist" value={item.frist_text || 'Nicht hinterlegt'} />
        <Info label="Kündigungsdatum" value={item.kuendigungsdatum || 'Nicht berechnet'} />
        <Info label="Monatskosten" value={formatEuro(item.monatliche_kosten)} />
        <Info label="Jahreskosten" value={formatEuro(item.jahreskosten)} />
      </InfoPanel>
    </div>
  );
}

function TemplateStep({ templates, selected, onSelect }) {
  return (
    <div style={templateGrid}>
      {templates.map(template => (
        <button key={template.id} type="button" onClick={() => onSelect(template.id)} style={templateCard(selected === template.id)}>
          <div style={{ color: '#f8fafc', fontWeight: 900, fontSize: 17 }}>{template.titel}</div>
          <p style={{ color: '#94a3b8', margin: '8px 0 12px', lineHeight: 1.45 }}>{template.beschreibung}</p>
          <div style={templateSnippet}>{template.textbausteine?.einleitung}</div>
        </button>
      ))}
    </div>
  );
}

function PreviewStep({ preview, onReload }) {
  if (!preview) {
    return (
      <div>
        <EmptyState text="Noch keine Vorschau geladen." />
        <button type="button" onClick={onReload} className="btn-primary" style={{marginTop:12}}>Vorschau erstellen</button>
      </div>
    );
  }
  return <LetterPreview preview={preview} />;
}

function CheckStep({ preview }) {
  return (
    <div style={twoColumn}>
      <InfoPanel title="Prüfpunkte">
        <CheckLine ok={Boolean(preview?.empfaenger?.adresse)} text="Empfängeradresse vorbereitet" />
        <CheckLine ok={Boolean(preview?.vertragsnummer)} text="Vertragsnummer vorhanden" />
        <CheckLine ok={Boolean(preview?.frist?.kuendigungsdatum)} text="Kündigungsdatum berechnet" />
        <CheckLine ok={Boolean(preview?.pdf?.renderer_ready)} text="PDF-Datenstruktur vorbereitet" />
      </InfoPanel>
      <InfoPanel title="Status">
        <Info label="Vorlage" value={preview?.vorlage?.titel || 'Nicht geladen'} />
        <Info label="Version" value={preview?.version || 1} />
        <Info label="Status" value={preview?.status || 'vorbereitet'} />
        <Info label="Erstellt am" value={formatDateTime(preview?.erstellt_am)} />
      </InfoPanel>
    </div>
  );
}

function DoneStep({ preview }) {
  return (
    <div style={doneBox}>
      <div style={{ fontSize: 42, marginBottom: 10 }}>✓</div>
      <h3 style={{ color: '#f8fafc', margin: 0, fontSize: 22 }}>Kündigung vorbereitet</h3>
      <p style={{ color: '#cbd5e1', margin: '10px auto 0', maxWidth: 560 }}>
        Die Kündigungsdaten sind vollständig strukturiert und können in Sprint 5 direkt an einen PDF-Renderer übergeben werden.
      </p>
      <div style={{ marginTop: 16, color: '#94a3b8', fontSize: 13 }}>
        Vorlage: {preview?.vorlage?.titel || 'Standard'} · Version {preview?.version || 1}
      </div>
    </div>
  );
}

function LetterPreview({ preview }) {
  return (
    <div style={letterGrid}>
      <InfoPanel title="Briefkopf">
        <Info label="Absender" value={preview.absender?.adresse || 'Nicht hinterlegt'} pre />
        <Info label="Empfänger" value={preview.empfaenger?.adresse || 'Nicht hinterlegt'} pre />
        <Info label="Ort / Datum" value={`${preview.ort || 'Ort'}, ${preview.datum || ''}`} />
      </InfoPanel>
      <InfoPanel title="Vertrag">
        <Info label="Vertragsnummer" value={preview.vertragsnummer || 'Nicht hinterlegt'} />
        <Info label="Kundennummer" value={preview.kundennummer || 'Nicht hinterlegt'} />
        <Info label="Kündigungsfrist" value={preview.frist?.text || 'Nicht hinterlegt'} />
        <Info label="Kündigungsdatum" value={preview.kuendigungsdatum || 'Nicht berechnet'} />
      </InfoPanel>
      <InfoPanel title="Anschreiben" wide>
        <Info label="Betreff" value={preview.betreff} />
        <Info label="Text" value={preview.anschreiben} pre />
        <Info label="Bitte um Bestätigung" value={preview.bitte_um_bestaetigung} />
        <Info label="Datenschutz" value={preview.datenschutztext} />
        <Info label="Grußformel" value={preview.grussformel} />
        <Info label="Anlagen" value={(preview.anlagen || []).join(', ') || 'Keine Anlagen'} />
      </InfoPanel>
    </div>
  );
}

function DeadlineRow({ item, onPrepare }) {
  const tone = toneMap(item.status_tone || item.status);
  return (
    <button type="button" onClick={() => onPrepare(item)} style={deadlineRow}>
      <span style={{ ...trafficLight, background: tone }} />
      <span style={{ minWidth: 0, flex: 1 }}>
        <strong style={{ color: '#f8fafc', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.titel}</strong>
        <span style={{ color: '#94a3b8', fontSize: 12 }}>{item.anbieter || 'Ohne Anbieter'} · {item.vertragsnummer || 'Ohne Nummer'}</span>
      </span>
      <span style={{ color: '#cbd5e1', fontSize: 13 }}>{item.kuendigungsdatum || 'Offen'}</span>
    </button>
  );
}

function HistoryList({ items }) {
  if (!items.length) return <EmptyState text="Noch keine vorbereiteten Kündigungen in der Historie." />;
  return (
    <div style={deadlineList}>
      {items.slice(0, 8).map(item => (
        <div key={item.id} style={historyRow}>
          <span>
            <strong style={{ color: '#f8fafc' }}>{item.titel}</strong>
            <span style={{ display: 'block', color: '#94a3b8', fontSize: 12 }}>
              {formatDateTime(item.erstellt_am)} · Vorlage {item.verwendete_vorlage} · Version {item.version}
            </span>
          </span>
          <span style={statusChip}>{item.status}</span>
        </div>
      ))}
    </div>
  );
}

function Section({ title, count, children }) {
  return (
    <section style={sectionStyle}>
      <div style={sectionHeader}>
        <h2 style={sectionTitle}>{title}</h2>
        <span style={countBadge}>{count}</span>
      </div>
      {children}
    </section>
  );
}

function KpiCard({ label, value, tone }) {
  return (
    <div style={{ ...kpiCard, borderColor: `${tone}66` }}>
      <div style={{ color: tone, fontSize: 26, fontWeight: 900 }}>{value}</div>
      <div style={{ color: '#cbd5e1', fontSize: 12, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function DashboardMetric({ label, value }) {
  return (
    <div style={metricBox}>
      <span style={{ color: '#94a3b8', fontSize: 12 }}>{label}</span>
      <strong style={{ color: '#f8fafc', fontSize: 20 }}>{value}</strong>
    </div>
  );
}

function InfoPanel({ title, children, wide }) {
  return (
    <div style={{ ...infoPanel, gridColumn: wide ? '1 / -1' : undefined }}>
      <h3 style={panelTitle}>{title}</h3>
      <div style={infoStack}>{children}</div>
    </div>
  );
}

function Info({ label, value, pre }) {
  return (
    <div style={infoItem}>
      <span style={infoLabel}>{label}</span>
      <span style={{ ...infoValue, whiteSpace: pre ? 'pre-wrap' : 'normal' }}>{value || '–'}</span>
    </div>
  );
}

function CheckLine({ ok, text }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', color: ok ? '#bbf7d0' : '#fecaca', fontSize: 14 }}>
      <span>{ok ? '✓' : '!'}</span>
      <span>{text}</span>
    </div>
  );
}

function EmptyState({ text }) {
  return <div style={emptyState}>{text}</div>;
}

function StateScreen({ text }) {
  return (
    <div style={{ padding: 20, maxWidth: 760, margin: '0 auto' }}>
      <Section title="Kündigungscenter" count="!">
        <EmptyState text={text} />
      </Section>
    </div>
  );
}

function toneMap(value) {
  return {
    danger: '#f87171',
    warning: '#fbbf24',
    success: '#34d399',
    info: '#38bdf8',
    ABGESCHLOSSEN: '#34d399',
    VERSENDET: '#34d399',
    BESTAETIGT: '#34d399',
    KUENDIGUNG_EMPFOHLEN: '#f87171',
    KUENDIGUNG_ERSTELLT: '#fbbf24',
    KUENDBAR: '#38bdf8',
    NICHT_KUENDBAR: '#64748b',
  }[value] || '#94a3b8';
}

function formatEuro(value) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(value) || 0);
}

function formatDateTime(value) {
  if (!value) return 'Nicht gespeichert';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

const pageStyle = { padding: 18, maxWidth: 1320, margin: '0 auto' };
const heroStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 18, background: '#172033', border: '1px solid #334155', borderRadius: 8, padding: 24, marginBottom: 16, boxShadow: '0 18px 45px rgba(0,0,0,0.22)' };
const eyebrow = { color: '#93c5fd', fontSize: 12, textTransform: 'uppercase', fontWeight: 900, letterSpacing: 0, marginBottom: 8 };
const heroTitle = { color: '#f8fafc', fontSize: 34, lineHeight: 1.08, margin: 0, letterSpacing: 0 };
const heroText = { color: '#cbd5e1', fontSize: 15, lineHeight: 1.55, margin: '12px 0 0', maxWidth: 720 };
const heroKpis = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))', gap: 10 };
const kpiCard = { background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 14, minWidth: 0 };
const dashboardBand = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 };

const metricBox = { background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 };
const tabBar = { display: 'flex', gap: 8, overflowX: 'auto', padding: '4px 0 14px' };
const tabButton = active => ({ flex: '0 0 auto', background: active ? '#818cf8' : '#1e293b', color: active ? '#fff' : '#cbd5e1', border: active ? '1px solid #818cf8' : '1px solid #334155', borderRadius: 8, padding: '10px 14px', cursor: 'pointer', fontWeight: 800, fontSize: 13 });
const sectionGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))', gap: 14 };
const sectionStyle = { background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 16, marginBottom: 14, minWidth: 0 };
const sectionHeader = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 };
const sectionTitle = { color: '#f8fafc', fontSize: 17, margin: 0, fontWeight: 900 };
const countBadge = { color: '#cbd5e1', background: '#0f172a', border: '1px solid #334155', borderRadius: 999, minWidth: 30, textAlign: 'center', padding: '4px 9px', fontSize: 12, fontWeight: 900 };
const contractGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 290px), 1fr))', gap: 12 };
const contractCard = { background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 16, minHeight: 260, display: 'flex', flexDirection: 'column', gap: 12 };
const cardTop = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
const trafficLight = { width: 12, height: 12, borderRadius: 999, flex: '0 0 auto', boxShadow: '0 0 0 4px rgba(255,255,255,0.04)' };
const badge = { borderRadius: 999, padding: '5px 9px', fontSize: 11, fontWeight: 900, textTransform: 'uppercase' };
const daysBadge = { color: '#cbd5e1', background: '#0f172a', border: '1px solid #334155', borderRadius: 999, padding: '5px 9px', fontSize: 11, fontWeight: 800 };
const provider = { color: '#93c5fd', fontSize: 13, fontWeight: 900 };
const contractTitle = { color: '#f8fafc', margin: 0, fontSize: 18, lineHeight: 1.25, minHeight: 46 };
const infoGrid = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 9, flex: 1 };
const infoItem = { minWidth: 0 };
const infoLabel = { display: 'block', color: '#94a3b8', fontSize: 11, marginBottom: 3 };
const infoValue = { display: 'block', color: '#f8fafc', fontSize: 13, fontWeight: 800, overflowWrap: 'anywhere' };

const deadlineList = { display: 'grid', gap: 9 };
const deadlineRow = { width: '100%', display: 'flex', alignItems: 'center', gap: 12, background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 12, cursor: 'pointer', textAlign: 'left' };
const historyRow = { display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 12 };
const statusChip = { color: '#bbf7d0', background: '#14532d', borderRadius: 999, padding: '5px 9px', fontSize: 12, fontWeight: 800 };
const modalBackdrop = { position: 'fixed', inset: 0, background: 'rgba(2, 6, 23, 0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 90 };
const modalCard = { width: 'min(1120px, 100%)', maxHeight: '90vh', overflowY: 'auto', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 18 };
const modalHeader = { display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 14 };
const modalTitle = { color: '#f8fafc', margin: 0, fontSize: 22 };
const stepper = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 130px), 1fr))', gap: 8, marginBottom: 14 };
const stepItem = active => ({ display: 'flex', alignItems: 'center', gap: 8, color: active ? '#f8fafc' : '#64748b', background: active ? '#1e3a8a' : '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 9, fontSize: 12, fontWeight: 800 });
const stepCircle = active => ({ width: 22, height: 22, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: active ? '#60a5fa' : '#1f2937', color: active ? '#0f172a' : '#94a3b8', fontWeight: 900 });
const wizardBody = { minHeight: 300 };
const wizardFooter = { display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 16, borderTop: '1px solid #334155', paddingTop: 14 };

const secondaryButton = disabled => ({ background: '#1e293b', color: disabled ? '#64748b' : '#f8fafc', border: '1px solid #334155', borderRadius: 8, padding: '11px 16px', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 800 });

const twoColumn = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 12 };
const templateGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 230px), 1fr))', gap: 12 };
const templateCard = active => ({ textAlign: 'left', background: active ? '#172554' : '#0f172a', border: active ? '1px solid #60a5fa' : '1px solid #263244', borderRadius: 8, padding: 14, cursor: 'pointer' });
const templateSnippet = { color: '#cbd5e1', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 10, fontSize: 12, lineHeight: 1.45 };
const letterGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 12 };
const infoPanel = { background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 14 };
const panelTitle = { color: '#f8fafc', fontSize: 15, margin: '0 0 12px', fontWeight: 900 };
const infoStack = { display: 'grid', gap: 10 };
const emptyState = { color: '#94a3b8', background: '#0f172a', border: '1px dashed #334155', borderRadius: 8, padding: 16, fontSize: 14 };
const doneBox = { textAlign: 'center', background: '#052e16', border: '1px solid #166534', borderRadius: 8, padding: 28 };
